import {Box, ChevronUpIcon, Divider} from 'native-base';
import {CustomText, H4Text, H5Text} from '@buncha/components/core/text';
import React, {useCallback, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BarcodeScanIcon from '@buncha/components/core/icon/BarcodeScanIcon';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {CustomFlatList} from '@buncha/components/core/list';
import {HBox} from '@buncha/components/core/view';
import If from '@buncha/components/conditional/If';
import MinusTickIcon from '@buncha/components/core/icon/MinusTickIcon';
import {Modules} from '@buncha/utils/types';
import RightTickIcon from '@buncha/components/core/icon/RightTickIcon';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {StyleSheet} from 'react-native';
import {Touchable} from '@buncha/components/core/touchable';
import {formatCurrency} from '@buncha/utils/common';
import {getPriceFromNSC2} from '@buncha/batchDetails/utils/ScannedAddItem';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  barcodes: string[];
  removeBarcode: (barcode: string) => void;
}

const BarcodeList = (props: Props) => {
  const {barcodes, removeBarcode} = props;
  const theme = useColorThemeType('main');
  const muliFont = useFontTheme('muli');
  const {body} =
    useGetLocale(Modules.itemDetails, ItemDetailsScreen.NSCItemScanner) ?? {};

  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);

  const toggleBottomSheet = useCallback(() => {
    setBottomSheetOpen(open => !open);
  }, []);

  const renderItem = useCallback(
    ({item}: {item: string}) => {
      return (
        <HBox>
          <Box style={styles.barcodeDetails}>
            <H5Text
              style={{
                fontFamily: muliFont[700].normal,
                color: theme?.text?.gray[100],
              }}>
              {body?.price}
              {formatCurrency(getPriceFromNSC2(item) ?? 0)}
            </H5Text>
            <CustomText
              style={{
                fontFamily: muliFont[700].normal,
                color: theme?.text?.gray[100],
              }}>
              {body?.barcode}
              {item}
            </CustomText>
          </Box>
          <HBox style={styles.iconsContainer}>
            <Touchable onPress={() => removeBarcode(item)}>
              <MinusTickIcon
                width={24}
                height={24}
                viewBox="-3 -3 26 26"
                fill={theme?.background.red[100]}
              />
            </Touchable>
            <Box
              style={[
                styles.absoluteContainer,
                {
                  backgroundColor: theme?.background?.gray?.[200],
                },
              ]}>
              <RightTickIcon
                style={[styles.checkIcon]}
                width={16}
                height={16}
                viewBox="-3 -1 25 25"
                fill={theme?.background.blue[100]}
              />
              <BarcodeScanIcon
                height={24}
                width={24}
                viewBox="0 0 14 14"
                fill={theme?.icon?.blue?.[300]}
              />
            </Box>
          </HBox>
        </HBox>
      );
    },
    [
      body?.barcode,
      body?.price,
      muliFont,
      removeBarcode,
      theme?.background.blue,
      theme?.background?.gray,
      theme?.background.red,
      theme?.icon?.blue,
      theme?.text?.gray,
    ],
  );

  const separator = useCallback(() => {
    return <Divider style={[styles.divider]} />;
  }, []);

  return (
    <If condition={barcodes?.length}>
      <Touchable onPress={toggleBottomSheet}>
        <HBox
          style={[
            styles.barcodes,
            {
              backgroundColor: theme?.background?.yellow?.[100],
            },
          ]}>
          <CustomText
            numberOfLines={2}
            style={{
              fontFamily: muliFont[700].normal,
              color: theme?.text.blue[300],
            }}>
            Do you have multiple barcodes for this weighted item?
          </CustomText>
          <ChevronUpIcon color={theme?.icon?.blue?.[300]} />
        </HBox>
      </Touchable>
      <BottomSheet open={bottomSheetOpen} onClose={toggleBottomSheet}>
        <Box style={styles.bottomSheetContainer}>
          <H4Text
            style={[
              styles.headerText,
              {
                color: theme?.text?.blue?.[300],
              },
            ]}>
            Scanned Barcodes:-
          </H4Text>

          <CustomFlatList
            data={barcodes}
            renderItem={renderItem}
            ItemSeparatorComponent={separator}
            showsVerticalScrollIndicator={false}
          />
        </Box>
      </BottomSheet>
    </If>
  );
};

export default React.memo(BarcodeList);

const styles = StyleSheet.create({
  barcodes: {
    width: '100%',
    padding: 10,
    paddingHorizontal: 20,
  },
  headerText: {
    textAlign: 'left',
    marginBottom: 20,
  },
  bottomSheetContainer: {
    width: '100%',
    padding: 10,
  },
  barcodeDetails: {alignItems: 'flex-start'},
  checkIcon: {
    top: -2,
    left: -2,
    position: 'absolute',
    zIndex: 2,
  },
  buttonsContainer: {
    padding: 10,
    gap: 10,
  },
  absoluteContainer: {
    position: 'relative',
    padding: 5,
    borderRadius: 100,
  },
  iconsContainer: {
    alignItems: 'center',
    gap: 10,
  },
  divider: {
    marginVertical: 10,
  },
});
